<template>
    <div>
        <el-form
                ref="form"
                :model="form"
                :rules="rules"
                v-loading="isBusy"
                element-loading-spinner="el-icon-loading"
                label-position="left"
                label-width="80px"
        >
            <el-row :gutter="40">
                <el-col :span="12">
                    <el-form-item label="Brand">
                        <el-select
                                :disabled="!isFieldAvailable('brand_id')"
                                v-model="brandId"
                                class="w-100"
                                placeholder="Brand"
                                clearable
                        >
                            <el-option
                                    v-for="{label, value} in brandOptions"
                                    :key="value"
                                    :label="label"
                                    :value="value"
                            ></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Message" prop="body">
                        <el-input
                                :disabled="!isFieldAvailable('body')"
                                type="textarea"
                                ref="body"
                                v-model="form.body"
                                :autosize="{minRows: 10, maxRows: 12}"
                                placeholder="Message"
                                class="message-input"
                        ></el-input>
                        <message-tools
                                @insert-value="insertValueIntoMessage"
                                :disabled="!isFieldAvailable('body')"
                        ></message-tools>
                    </el-form-item>
                    <el-form-item prop="start_at">
                        <template v-slot:label>
                            <el-tooltip
                                    effect="light"
                                    content="Scheduled date and time to begin the campaign"
                                    placement="top-start"
                            >
                                <span>Start <i class="far fa-question-circle"></i></span>
                            </el-tooltip>
                        </template>
                        <start-input
                                :disabled="!isFieldAvailable('start_at')"
                                placeholder="Start"
                                v-model="form.start_at"
                        ></start-input>

                    </el-form-item>
                    <el-form-item prop="status" label="Status">
                        <el-select v-model="form.status" class="w-100" :disabled="!isFieldAvailable('status')">
                            <el-option
                                    v-for="{label, value} in statusOptions"
                                    :key="value"
                                    :label="label"
                                    :value="value"
                            ></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item prop="max_messages">
                        <template v-slot:label>
                            <el-tooltip
                                    effect="light"
                                    content="Send Messages Limit"
                                    placement="top-start"
                            >
                                <span>Limit <i class="far fa-question-circle"></i></span>
                            </el-tooltip>
                        </template>
                        <el-input
                                :disabled="!isFieldAvailable('max_messages')"
                                v-model="form.max_messages"
                                placeholder="Send Messages Limit"
                        ></el-input>
                    </el-form-item>
                    <el-form-item prop="min_interval">
                        <template v-slot:label>
                            <el-tooltip
                                    effect="light"
                                    content="Minimal interval since last contact in days"
                                    placement="top-start"
                            >
                                <span>Interval <i class="far fa-question-circle"></i></span>
                            </el-tooltip>
                        </template>
                        <min-interval-select
                                clearable
                                class="w-100"
                                :disabled="!isFieldAvailable('min_interval')"
                                v-model="form.min_interval"
                                placeholder="Time since last contact"
                        ></min-interval-select>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item prop="outgoing_number_id" label="Sender">
                        <outgoing-number-select
                                :disabled="!isFieldAvailable('outgoing_number_id')"
                                v-model="form.outgoing_number_id"
                                :brand-id="brandId"
                                :model="model"
                                :url="searchOutgoingNumbersUrl"
                                class="w-100"
                                placeholder="Outgoing Number"
                        ></outgoing-number-select>
                    </el-form-item>
                    <el-form-item prop="contact_list_id" label="Recipient">
                        <contact-list-select
                                :disabled="!isFieldAvailable('contact_list_id')"
                                v-model="form.contact_list_id"
                                :brand-id="brandId"
                                :model="model"
                                :url="searchContactListUrl"
                                class="w-100"
                                placeholder="Contact List"
                        ></contact-list-select>
                    </el-form-item>
                </el-col>
            </el-row>
        </el-form>
        <button class="btn btn-create px-4" @click="store">Store</button>
    </div>
</template>

<script>
  import BaseForm from '../form';
  import InputMixin from '../../mixins/inputs';
  import OutgoingNumberSelect from './inputs/outgoing-number-select';
  import ContactListSelect from './inputs/contact-list-select';
  import MinIntervalSelect from '../shared/inputs/min-interval-select';
  import StartInput from './inputs/start';
  import MessageTools from '../shared/message-tools';
  import {isParsableNumber} from "../../helpers";

  export default {
    extends: BaseForm,

    components: {
      MessageTools,
      OutgoingNumberSelect,
      ContactListSelect,
      MinIntervalSelect,
      StartInput,
    },

    mixins: [
      InputMixin,
    ],

    props: {
      brands: Array,
      initialStatusOptions: Array,
      initialContactLists: Array,
      searchOutgoingNumbersUrl: String,
      searchContactListUrl: String,
      initialFieldsAvailability: Array,
    },

    data() {
      const form = {
        campaign_template_id: null,
        status: null,
        outgoing_number_id: null,
        contact_list_id: null,
        start_at: null,
        stop_at: null,
        body: null,
        min_interval: null,
        max_messages: null,
      };

      return {
        form,
        rules: _.mapValues(form, () => [{validator: this.fieldExternalValidator}]),
        brandId: null,
        statusOptions: this.initialStatusOptions || [],
        contactLists: this.initialContactLists || [],
        fieldsAvailability: this.initialFieldsAvailability || [],
      };
    },

    computed: {
      /**
       * @return {Array}
       */
      brandOptions() {
        return _.map(this.brands || [], ({id, title}) => {
          return {
            value: id,
            label: title,
          };
        })
      },
    },

    watch: {
      model: {
        immediate: true,
        handler: function (val) {
          let brand = _.findKey(this.brandOptions, ['value', _.get(val, 'outgoing_number.brand_id', null)]);
          if (!_.isUndefined(brand)) {
            this.brandId = _.get(val, 'outgoing_number.brand_id', null);
          }
        },
      },
    },

    methods: {
      /**
       * @param {Object} model
       */
      initForm(model) {
        const form = _.mapValues(this.form, (val, prop) => _.get(model, prop, null));
        const overrides = {
          body: this.clearStr(_.get(model, 'body', '') || ''),
          start_at: _.get(model, 'start_at.datetime', null),
          stop_at: _.get(model, 'stop_at.datetime', null),
          min_interval: this.secondsToDays(_.get(model, 'min_interval', null)),
        };

        this.form = _.defaults(overrides, form);
      },

      /**
       * @param {string} val
       * @return {string}
       */
      clearStr(val) {
        val = String(val).trimEnd();

        return val ? val.replace(/\r\n/g, "\n") : val;
      },

      /**
       * @return {number|undefined}
       */
      secondsToDays(value) {
        if (_.isFinite(value)) {
          return _.floor(moment.duration(value, 'seconds').asDays());
        }
      },

      /**
       * @return {number|null}
       */
      daysToSeconds(value) {
        if (isParsableNumber(value)) {
          return moment.duration(Number(value), 'days').asSeconds();
        }

        return value;
      },

      /**
       * @return {Object}
       */
      getForm() {
        return _.assign({}, this.form, {
          _method: this.method,
          min_interval: this.daysToSeconds(this.form.min_interval),
        });
      },

      /**
       * @param {string} value
       */
      insertValueIntoMessage(value) {
        this.addTextInCursorPosition(
          _.get(this.$refs, 'body.$refs.textarea'),
          _.toString(this.form.body),
          value,
          this.messageSetter
        );
      },

      /**
       * @param {string} value
       */
      messageSetter(value) {
        this.form.body = value;
      },

      /**
       * @param {string} field
       * @return {boolean}
       */
      isFieldAvailable(field) {
        return _.includes(this.fieldsAvailability, '*')
          || _.includes(this.fieldsAvailability, field);
      }
    }
  }
</script>
